import { NavLink, useMatch } from "react-router-dom";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import { useGetFacilityEventsQuery } from "../api/endpoints/events";

/** Mounted only on the /events sub-pages; the dashboard keeps its own crumb. */
export function EventsBreadcrumb() {
  const detailMatch = useMatch("/events/:eventId");
  const editMatch = useMatch("/events/:eventId/edit");

  const rawEventId = editMatch?.params.eventId ?? detailMatch?.params.eventId;
  const isNew = rawEventId === "new";
  const eventId = rawEventId && !isNew ? rawEventId : undefined;
  const isEdit = !!editMatch;

  const facilityId = useGetCurrentLocalFacilityId();
  // The facility list is already cached by the dashboard; this adds no call.
  const { data: eventsResult } = useGetFacilityEventsQuery(
    { facilityId, includePublic: true },
    { skip: !facilityId || !eventId },
  );
  const eventName =
    eventsResult?.events.find((e) => String(e.id) === eventId)?.name ?? "Event";

  const pageLabel = isNew ? "New Event" : isEdit ? "Edit" : eventName;

  // Event names run long; keep the bar on one line and truncate the name
  // crumbs rather than wrapping the whole thing.
  const nameCrumb =
    "block max-w-[8rem] truncate sm:max-w-[12rem] lg:max-w-[18rem]";

  return (
    <>
      <BreadcrumbItem className="hidden shrink-0 md:block">
        <BreadcrumbLink
          className="whitespace-nowrap"
          render={<NavLink to="/events" />}
        >
          Events
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator className="hidden md:block" />
      {isEdit && eventId && (
        <>
          <BreadcrumbItem className="hidden min-w-0 md:block">
            <BreadcrumbLink
              className={nameCrumb}
              title={eventName}
              render={<NavLink to={`/events/${eventId}`} />}
            >
              {eventName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden md:block" />
        </>
      )}
      <BreadcrumbItem className="min-w-0">
        <BreadcrumbPage className={nameCrumb} title={pageLabel}>
          {pageLabel}
        </BreadcrumbPage>
      </BreadcrumbItem>
    </>
  );
}
